/**
 * Carrier MCP — prepaid wallet top-up + auto-top-up layer.
 *
 * Funds the ATLAS prepaid wallet via Stripe and lets a Claude/agent session
 * drive top-ups. The wallet itself (balance, ledger, auto-top-up config) lives
 * in ATLAS — see wallet-client.ts for the HTTP boundary. This module only:
 *   1. defines the bulk prepaid pack catalog (PACKS),
 *   2. creates Stripe Checkout sessions (mode:'payment') to buy a pack,
 *   3. runs the off-session auto-top-up charge (reusable; cron-callable),
 *   4. registers three MCP tools so agents can read balance and top up.
 *
 * Stripe lifecycle crediting happens in stripe-webhook.ts (checkout.session.completed
 * / payment_intent.succeeded with metadata.kind === 'wallet_topup'). This module
 * never credits ATLAS directly for Checkout — only the off-session auto-top-up
 * path credits ATLAS inline (its PaymentIntent is confirmed synchronously here).
 *
 * KV lookups reuse the existing flat-key billing pattern (see billing.ts):
 *   stripe_customer_id:<sub>  → Stripe customer ID
 *   stripe_pm_id:<sub>        → saved default payment method ID
 * keyed by the Clerk user sub (billing stores per-user, not per-org).
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import * as Sentry from "@sentry/cloudflare";
import type { Env, CarrierProps, ToolScope } from "./types.js";
import { writeAudit } from "./audit.js";
import { annotationsFor } from "./tool-annotations.js";
import { getWallet, walletTopup, WalletClientError } from "./wallet-client.js";
import { resolveBillingSub } from "./billing-sub.js";
import { denyUnlessScoped } from "./scope-guard.js";

// ---------------------------------------------------------------------------
// Pack catalog
// ---------------------------------------------------------------------------

/**
 * A bulk prepaid wallet pack. `eurCents` is what the customer pays; the wallet
 * is credited `eurCents + bonusCents` (bonus = eurCents × bonusPct / 100).
 * Percentages are placeholders — tune here; everything downstream derives from
 * this table.
 */
export interface WalletPack {
  /** Stable pack identifier used in Checkout metadata + UI selection. */
  id: string;
  /** Amount charged, in EUR cents. */
  eurCents: number;
  /** Bonus credit percentage applied on top of the paid amount. */
  bonusPct: number;
}

/** Bulk prepaid pack catalog (EUR). Larger pack → bigger bonus. Tunable. */
export const PACKS: readonly WalletPack[] = [
  { id: "pack_500", eurCents: 50_000, bonusPct: 0 },
  { id: "pack_1000", eurCents: 100_000, bonusPct: 3 },
  { id: "pack_2500", eurCents: 250_000, bonusPct: 6 },
  { id: "pack_5000", eurCents: 500_000, bonusPct: 10 },
] as const;

/** Bonus credit (EUR cents) for a given paid amount. Floors to whole cents. */
export function bonusCentsFor(pack: WalletPack): number {
  return Math.floor((pack.eurCents * pack.bonusPct) / 100);
}

/** Resolve a pack by its catalog id, or by its exact paid EUR-cents amount. */
export function resolvePack(idOrCents: string | number): WalletPack | undefined {
  if (typeof idOrCents === "number") {
    return PACKS.find((p) => p.eurCents === idOrCents);
  }
  return (
    PACKS.find((p) => p.id === idOrCents) ??
    PACKS.find((p) => String(p.eurCents) === idOrCents)
  );
}

// ---------------------------------------------------------------------------
// Stripe helpers (edge-compatible — form-encoded REST, no stripe npm package)
// ---------------------------------------------------------------------------

export const STRIPE_API = "https://api.stripe.com";

export async function stripePostForm<T = unknown>(
  stripeKey: string,
  path: string,
  params: URLSearchParams,
  opts?: { idempotencyKey?: string },
): Promise<{ ok: boolean; status: number; data: T }> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${stripeKey}`,
    "Content-Type": "application/x-www-form-urlencoded",
  };
  if (opts?.idempotencyKey) {
    headers["Idempotency-Key"] = opts.idempotencyKey;
  }
  const res = await fetch(`${STRIPE_API}${path}`, {
    method: "POST",
    headers,
    body: params.toString(),
  });
  const data = (await res.json()) as T;
  return { ok: res.ok, status: res.status, data };
}

// ---------------------------------------------------------------------------
// Checkout session creation (one-time bulk pack purchase)
// ---------------------------------------------------------------------------

export interface CreateTopupCheckoutArgs {
  orgId: string;
  pack: WalletPack;
  /** Override success URL; defaults to env.WALLET_CHECKOUT_SUCCESS_URL. */
  successUrl?: string;
  /** Override cancel URL; defaults to env.WALLET_CHECKOUT_CANCEL_URL. */
  cancelUrl?: string;
}

export interface TopupCheckoutResult {
  id: string;
  url: string | null;
}

const DEFAULT_SUCCESS_URL = "https://app.carrier.llc/billing?topup=success";
const DEFAULT_CANCEL_URL = "https://app.carrier.llc/billing?topup=cancelled";

/**
 * Create a Stripe Checkout session (mode:'payment') for a bulk prepaid pack.
 * Wallet crediting is deferred to the webhook on completion — the metadata here
 * (kind/org_id/pack_eur_cents/bonus_eur_cents) is what stripe-webhook.ts reads.
 */
export async function createTopupCheckout(
  env: Env,
  args: CreateTopupCheckoutArgs,
): Promise<TopupCheckoutResult> {
  const stripeKey = env.STRIPE_SECRET_KEY;
  if (!stripeKey) throw new Error("Stripe not configured (STRIPE_SECRET_KEY absent)");

  const bonusCents = bonusCentsFor(args.pack);
  const successUrl =
    args.successUrl ?? env.WALLET_CHECKOUT_SUCCESS_URL ?? DEFAULT_SUCCESS_URL;
  const cancelUrl =
    args.cancelUrl ?? env.WALLET_CHECKOUT_CANCEL_URL ?? DEFAULT_CANCEL_URL;

  const bonusLabel = bonusCents > 0 ? ` (+€${(bonusCents / 100).toFixed(2)} bonus)` : "";

  // Reuse the org's existing Stripe customer if one is already on file, so a
  // top-up never creates a SECOND Customer and clobbers the canonical
  // stripe_customer_id:<sub> shared by the subscription / portal / metered
  // flows. Only fall back to customer_creation:'always' for a first-time payer.
  // Resolution shared with internal-wallet/stripe-webhook via resolveBillingSub.
  // Guard before the KV read, for the same reason runAutoTopup does below:
  // stdio stubs CARRIER_USERS to null and `.get` off null throws a SYNCHRONOUS
  // TypeError, so the chained `.catch()` never runs. runAutoTopup was given
  // this guard and this path was not, so wallet_topup_checkout died with
  // "Cannot read properties of null (reading 'get')" even on a CLI that had a
  // perfectly good STRIPE_SECRET_KEY.
  //
  // It refuses rather than degrading to customer_creation:'always'. A session
  // made without the customer lookup mints a SECOND Stripe Customer, and the
  // wallet is credited by the hosted webhook, which reads the canonical
  // stripe_customer_id:<sub> this would clobber — see the note below. A local
  // process cannot see that record, so it has no business creating the session.
  if (!env.CARRIER_USERS) {
    throw new Error(
      "Wallet top-up is not available in local stdio mode: the prepaid wallet is " +
        "credited by the hosted Carrier service after payment, and this process cannot " +
        "reach the customer record that crediting depends on. Creating the session here " +
        "would bill the card against a duplicate Stripe customer and the wallet would " +
        "never be credited. Top up from the console at https://app.carrier.llc/billing, " +
        "or connect to the hosted MCP at https://mcp.carrier.llc/mcp, which has the " +
        "binding. `wallet_balance` still works here if ATLAS_BASE_URL and " +
        "CARRIER_INTERNAL_API_KEY are set.",
    );
  }
  const billingSub = await resolveBillingSub(env, args.orgId);
  const existingCustomer = await env.CARRIER_USERS
    .get(`stripe_customer_id:${billingSub}`)
    .catch(() => null);

  const params = new URLSearchParams({
    mode: "payment",
    success_url: successUrl,
    cancel_url: cancelUrl,
    "line_items[0][quantity]": "1",
    "line_items[0][price_data][currency]": "eur",
    "line_items[0][price_data][unit_amount]": String(args.pack.eurCents),
    "line_items[0][price_data][product_data][name]": `Carrier wallet top-up — €${(
      args.pack.eurCents / 100
    ).toFixed(2)}${bonusLabel}`,
    client_reference_id: args.orgId,
    "metadata[kind]": "wallet_topup",
    "metadata[org_id]": args.orgId,
    "metadata[pack_id]": args.pack.id,
    "metadata[pack_eur_cents]": String(args.pack.eurCents),
    "metadata[bonus_eur_cents]": String(bonusCents),
    // Save the payment method for reuse so subsequent off-session auto-top-up
    // charges (runAutoTopup) can charge it without the customer present.
    "payment_intent_data[setup_future_usage]": "off_session",
    // Mirror metadata onto the resulting PaymentIntent so the
    // payment_intent.succeeded webhook can also credit idempotently.
    "payment_intent_data[metadata][kind]": "wallet_topup",
    "payment_intent_data[metadata][org_id]": args.orgId,
    "payment_intent_data[metadata][pack_eur_cents]": String(args.pack.eurCents),
    "payment_intent_data[metadata][bonus_eur_cents]": String(bonusCents),
  });

  if (existingCustomer) {
    // Attach the top-up (and its saved PM) to the canonical customer.
    params.set("customer", existingCustomer);
  } else {
    // First-time payer: let Checkout create the durable Customer the saved PM
    // (setup_future_usage) attaches to.
    params.set("customer_creation", "always");
  }

  const result = await stripePostForm<{ id: string; url: string | null }>(
    stripeKey,
    "/v1/checkout/sessions",
    params,
  );
  if (!result.ok) {
    throw new Error(`Stripe checkout session create failed: ${JSON.stringify(result.data)}`);
  }
  return { id: result.data.id, url: result.data.url };
}

// ---------------------------------------------------------------------------
// Off-session auto-top-up charge (reusable — cron + MCP tool both call this)
// ---------------------------------------------------------------------------

export interface AutoTopupArgs {
  /** Org to top up. */
  orgId: string;
  /** Clerk sub — Stripe customer/PM keys are stored per user, not per org. */
  sub: string;
  /** Override the pack amount (EUR cents). Defaults to the org's configured pack. */
  packCents?: number;
}

export interface AutoTopupResult {
  charged: boolean;
  /** Amount charged + credited in EUR cents (pack + bonus), 0 when not charged. */
  creditedCents: number;
  /** Wallet balance after crediting, when known. */
  balanceAfterCents?: number;
  paymentIntentId?: string;
  /** Machine-readable reason when `charged` is false. */
  reason?: string;
}

/**
 * Charge the org's saved Stripe payment method off-session for a prepaid pack,
 * then credit ATLAS on success. Reusable from a cron (balance < threshold) and
 * from the wallet_auto_topup MCP tool.
 *
 * Pack selection precedence: explicit packCents → wallet auto_topup_pack_cents.
 * The chosen amount must match a catalog PACK so the bonus is well-defined.
 */
export async function runAutoTopup(
  env: Env,
  args: AutoTopupArgs,
): Promise<AutoTopupResult> {
  const stripeKey = env.STRIPE_SECRET_KEY;
  if (!stripeKey) {
    return { charged: false, creditedCents: 0, reason: "stripe_not_configured" };
  }

  // Resolve the configured pack from the wallet when not explicitly provided.
  let packCents = args.packCents;
  try {
    const wallet = await getWallet(env, args.orgId);
    // `?? undefined` because the wallet reports an unconfigured pack as null
    // while packCents is `number | undefined`. Both fall through the
    // `!packCents` guard below to "no_pack_configured" — this only keeps the
    // two spellings of "unset" from colliding in the type.
    if (packCents === undefined) packCents = wallet.auto_topup_pack_cents ?? undefined;
  } catch (e) {
    if (e instanceof WalletClientError) {
      return { charged: false, creditedCents: 0, reason: `wallet_unreachable: ${e.message}` };
    }
    throw e;
  }

  if (!packCents || packCents <= 0) {
    return { charged: false, creditedCents: 0, reason: "no_pack_configured" };
  }

  const pack = resolvePack(packCents);
  if (!pack) {
    return { charged: false, creditedCents: 0, reason: `unknown_pack: ${packCents}` };
  }

  // Guard before the KV read: stdio stubs CARRIER_USERS to null, and reading
  // `.get` off null throws synchronously, so the chained `.catch()` never runs.
  // No KV means no stored Stripe customer/PM, which is exactly "no payment
  // method" — degrade to that instead of throwing out of the tool.
  if (!env.CARRIER_USERS) {
    return { charged: false, creditedCents: 0, reason: "no_payment_method" };
  }
  const customerId = await env.CARRIER_USERS.get(`stripe_customer_id:${args.sub}`).catch(
    () => null,
  );
  const pmId = await env.CARRIER_USERS.get(`stripe_pm_id:${args.sub}`).catch(() => null);
  if (!customerId || !pmId) {
    return { charged: false, creditedCents: 0, reason: "no_payment_method" };
  }

  const bonusCents = bonusCentsFor(pack);

  const body = new URLSearchParams({
    amount: String(pack.eurCents),
    currency: "eur",
    customer: customerId,
    payment_method: pmId,
    confirm: "true",
    off_session: "true",
    description: `Carrier wallet auto-top-up — €${(pack.eurCents / 100).toFixed(2)}`,
    "metadata[kind]": "wallet_topup",
    "metadata[org_id]": args.orgId,
    "metadata[pack_eur_cents]": String(pack.eurCents),
    "metadata[bonus_eur_cents]": String(bonusCents),
    "metadata[auto_topup]": "true",
  });

  const resp = await stripePostForm<{ id: string; status: string }>(
    stripeKey,
    "/v1/payment_intents",
    body,
  );
  if (!resp.ok) {
    return {
      charged: false,
      creditedCents: 0,
      reason: `stripe_error: ${JSON.stringify(resp.data)}`,
    };
  }
  if (resp.data.status !== "succeeded") {
    return {
      charged: false,
      creditedCents: 0,
      reason: `payment_status: ${resp.data.status}`,
      paymentIntentId: resp.data.id,
    };
  }

  // Credit ATLAS inline. `ref` is the PaymentIntent id — ATLAS dedupes on it,
  // so a webhook replay for the same PI is a no-op on the ledger.
  const creditedCents = pack.eurCents + bonusCents;
  try {
    const topup = await walletTopup(env, args.orgId, {
      eurCents: creditedCents,
      ref: resp.data.id,
      type: "auto_topup",
    });
    return {
      charged: true,
      creditedCents,
      balanceAfterCents: topup.balance_after_cents,
      paymentIntentId: resp.data.id,
    };
  } catch (e) {
    // Charge succeeded but crediting failed — surface so it can be reconciled.
    // The PI metadata still carries kind=wallet_topup, so the webhook will
    // credit it idempotently on Stripe's payment_intent.succeeded delivery.
    Sentry.captureException(e);
    return {
      charged: true,
      creditedCents,
      paymentIntentId: resp.data.id,
      reason: `credited_via_webhook_pending: ${e instanceof Error ? e.message : "atlas error"}`,
    };
  }
}

// ---------------------------------------------------------------------------
// MCP tools
// ---------------------------------------------------------------------------

/** Scope map merged by generate-docs.mjs (parallels STRIPE_CONNECT_TOOL_SCOPES). */
export const WALLET_TOOL_SCOPES: Record<string, ToolScope> = {
  wallet_balance: "read",
  wallet_topup_checkout: "write",
  wallet_auto_topup: "write",
};

export interface WalletToolContext {
  env: Env;
  props: CarrierProps;
}

type ToolResult = {
  content: Array<{ type: "text"; text: string }>;
  isError?: boolean;
  structuredContent?: Record<string, unknown>;
  /** See the note on ScopeDenied in scope-guard.ts. */
  [k: string]: unknown;
};

/**
 * A success result that satisfies a declared `outputSchema`.
 *
 * Declaring `outputSchema` is never a one-line change: the SDK turns a call
 * into `isError: true` when a tool declares a schema and returns only text
 * (tests/output-schema-behaviour.test.ts). So the schema and this helper have
 * to arrive together.
 *
 * `content` keeps the exact JSON text callers already parse — the shape did not
 * change, it only became declared as well as returned.
 *
 * Error paths deliberately stay on `err()`. The SDK skips output validation on
 * an `isError` result, so a refusal does not have to carry a payload it has no
 * honest values for.
 */
function okStructured(payload: Record<string, unknown>): ToolResult {
  return {
    content: [{ type: "text", text: JSON.stringify(payload) }],
    structuredContent: payload,
  };
}

function err(text: string): ToolResult {
  return { isError: true, content: [{ type: "text", text }] };
}

export function registerWalletTools(server: McpServer, ctx: WalletToolContext): void {
  const { env, props } = ctx;
  const defaultOrgId = props.org_id ?? props.sub;

  // -------------------------------------------------------------------------
  // wallet_balance — read the ATLAS wallet (balance + auto-top-up config)
  // -------------------------------------------------------------------------
  server.registerTool(
    "wallet_balance",
    {
      title: "Wallet Balance",
      description:
        "Read the caller organisation's Carrier prepaid wallet: current balance and auto-top-up settings. " +
        "The wallet is what Carrier bills your own eSIM spend against — it is not an OCS balance and not your MCP plan credits. " +
        "Params: none (the organisation is taken from the session). " +
        "Returns: { org_id, balance_eur_cents, auto_topup_enabled, auto_topup_threshold_cents, auto_topup_pack_cents } — all amounts in EUR cents, not euros. " +
        "Do NOT use this for a reseller account balance in OCS — use `get_reseller_info` or `list_reseller_accounts`. " +
        "Do NOT use this for a subscriber's OCS balance — use `get_subscriber`. " +
        "Do NOT use this for MCP plan credits — use `credit_balance`. " +
        "Do NOT use this for money held at Stripe — use `stripe_connect_balance`.",
      inputSchema: {},
      // Derived from the object literal this handler builds below, not from the
      // description. All five keys are always present on the success path, so
      // none is optional — but the two auto-top-up amounts are nullable, not
      // just unset. ATLAS types them `number | null` and synthesises an
      // all-null row for an org with no wallet yet (its toWalletView), so an
      // org that has never configured auto-top-up — every newly onboarded one
      // — returns null here. Requiring a number turned that, the most common
      // state a new operator is in, into an output validation error.
      outputSchema: {
        org_id: z.string().describe("Carrier organisation the wallet belongs to"),
        balance_eur_cents: z.number().int().describe("Current balance in EUR cents"),
        auto_topup_enabled: z.boolean(),
        auto_topup_threshold_cents: z
          .number()
          .int()
          .nullable()
          .describe(
            "Balance at or below which an auto top-up fires, EUR cents. Null when auto-top-up is unconfigured.",
          ),
        auto_topup_pack_cents: z
          .number()
          .int()
          .nullable()
          .describe(
            "Amount an auto top-up charges, EUR cents. Null when auto-top-up is unconfigured.",
          ),
      },
      annotations: annotationsFor("wallet_balance", "read"),
    },
    async () => {
      // Call-time scope check. This file does not use wrapHandler,
      // so without this the tool is callable at any scope. See
      // scope-guard.ts.
      const denied = denyUnlessScoped(props, "wallet_balance", WALLET_TOOL_SCOPES);
      if (denied) return denied;

      const orgId = defaultOrgId;
      const start = Date.now();
      try {
        const wallet = await getWallet(env, orgId);
        writeAudit(env, {
          tool_name: "wallet_balance",
          ocs_method: "atlas.wallet.read",
          status: "ok",
          dry_run: false,
          duration_ms: Date.now() - start,
          sub: props.sub,
          reseller_id: props.reseller_id,
        });
        return okStructured({
          org_id: orgId,
          balance_eur_cents: wallet.balance_eur_cents,
          auto_topup_enabled: wallet.auto_topup_enabled,
          auto_topup_threshold_cents: wallet.auto_topup_threshold_cents,
          auto_topup_pack_cents: wallet.auto_topup_pack_cents,
        });
      } catch (e) {
        if (e instanceof WalletClientError) return err(`Wallet error: ${e.message}`);
        Sentry.captureException(e);
        return err(`Error: ${e instanceof Error ? e.message : "unknown"}`);
      }
    },
  );

  // -------------------------------------------------------------------------
  // wallet_topup_checkout — create a Stripe Checkout session for a bulk pack
  // -------------------------------------------------------------------------
  server.registerTool(
    "wallet_topup_checkout",
    {
      title: "Wallet Top-up Checkout",
      description:
        "Create a Stripe Checkout link so a human can top up the Carrier prepaid wallet by card. " +
        "This tool charges nothing itself: it returns a hosted URL, and the wallet is credited (pack amount + bonus) only after someone completes the payment in a browser. " +
        "Params: `pack` (optional — pack id `pack_500`/`pack_1000`/`pack_2500`/`pack_5000`, or an exact EUR-cents amount matching a catalog pack; omit to list the packs and their bonuses without creating a session). " +
        "Returns: { pack_id, pack_eur_cents, bonus_eur_cents, checkout_session_id, checkout_url } — amounts in EUR cents. " +
        "Do NOT use this when the wallet must be funded now with no human in the loop — use `wallet_auto_topup`, which charges the saved card immediately. " +
        "Do NOT use this to move money in OCS — `modify_account_balance` and `modify_subscriber_balance` are unrelated ledgers.",
      inputSchema: {
        pack: z
          .string()
          .optional()
          .describe(
            "Pack id (pack_500/pack_1000/pack_2500/pack_5000) or exact EUR-cents amount. Omit to list packs.",
          ),
      },
      /**
       * This tool really does return two different shapes, so the schema says
       * so rather than pretending otherwise. `pack` omitted returns the
       * catalogue arm (`packs`); `pack` supplied returns the session arm. An
       * `outputSchema` is an object shape, not a union type, so the two arms
       * are expressed as mutually exclusive optional groups and the pairing is
       * documented here. Marking either arm required would break the other.
       */
      outputSchema: {
        packs: z
          .array(
            z.object({
              id: z.string(),
              eur_cents: z.number().int(),
              bonus_pct: z.number(),
              bonus_eur_cents: z.number().int(),
              credited_eur_cents: z.number().int(),
            }),
          )
          .optional()
          .describe("Catalogue arm — present only when `pack` was omitted."),
        org_id: z.string().optional().describe("Session arm."),
        pack_id: z.string().optional().describe("Session arm."),
        pack_eur_cents: z.number().int().optional().describe("Session arm."),
        bonus_eur_cents: z.number().int().optional().describe("Session arm."),
        checkout_session_id: z.string().optional().describe("Session arm."),
        checkout_url: z
          .string()
          .nullable()
          .optional()
          .describe("Session arm. Stripe may return null for a session URL."),
      },
      annotations: annotationsFor("wallet_topup_checkout", "write"),
    },
    async ({ pack }) => {
      // Call-time scope check. This file does not use wrapHandler,
      // so without this the tool is callable at any scope. See
      // scope-guard.ts.
      const denied = denyUnlessScoped(props, "wallet_topup_checkout", WALLET_TOOL_SCOPES);
      if (denied) return denied;

      const orgId = defaultOrgId;
      const start = Date.now();

      if (!pack) {
        return okStructured({
          packs: PACKS.map((p) => ({
            id: p.id,
            eur_cents: p.eurCents,
            bonus_pct: p.bonusPct,
            bonus_eur_cents: bonusCentsFor(p),
            credited_eur_cents: p.eurCents + bonusCentsFor(p),
          })),
        });
      }

      const selected = resolvePack(pack);
      if (!selected) {
        return err(
          `Unknown pack "${pack}". Valid: ${PACKS.map((p) => p.id).join(", ")}.`,
        );
      }

      try {
        const session = await createTopupCheckout(env, { orgId, pack: selected });
        writeAudit(env, {
          tool_name: "wallet_topup_checkout",
          ocs_method: "stripe.checkout.session.create",
          status: "ok",
          dry_run: false,
          duration_ms: Date.now() - start,
          sub: props.sub,
          reseller_id: props.reseller_id,
        });
        return okStructured({
          org_id: orgId,
          pack_id: selected.id,
          pack_eur_cents: selected.eurCents,
          bonus_eur_cents: bonusCentsFor(selected),
          checkout_session_id: session.id,
          checkout_url: session.url,
        });
      } catch (e) {
        Sentry.captureException(e);
        return err(`Error: ${e instanceof Error ? e.message : "unknown"}`);
      }
    },
  );

  // -------------------------------------------------------------------------
  // wallet_auto_topup — off-session charge using the saved PM, credit ATLAS
  // -------------------------------------------------------------------------
  server.registerTool(
    "wallet_auto_topup",
    {
      title: "Wallet Auto Top-up (charges card now)",
      description:
        "Charge the organisation's saved card off-session, right now, and credit the Carrier prepaid wallet with the pack. " +
        "This spends real money without any browser step or human confirmation, so only call it when the user has asked to be charged. " +
        "Params: `pack_cents` (optional integer, EUR cents — must match a catalog pack; omit to use the organisation's configured auto-top-up pack). " +
        "Returns on success: { org_id, charged: true, credited_eur_cents, balance_after_cents, payment_intent_id } — amounts in EUR cents. " +
        "Returns { charged: false, reason } as an error when the card declines or no payment method is saved. " +
        "Do NOT use this to let a human pay in a browser — use `wallet_topup_checkout`, which only returns a link. " +
        "Do NOT use this to change the threshold that triggers automatic top-ups; this tool performs one charge and does not edit the configuration. " +
        "Do NOT use this to credit an OCS reseller account or subscriber — see `modify_account_balance` and `modify_subscriber_balance`.",
      inputSchema: {
        pack_cents: z
          .number()
          .int()
          .positive()
          .optional()
          .describe("Override pack amount in EUR cents (must match a catalog pack)."),
      },
      /**
       * Only the charged arm is described here — `charged: false` returns
       * through `err()`, and the SDK skips output validation on an `isError`
       * result.
       *
       * `balance_after_cents` is optional and the description above is wrong to
       * promise it. runAutoTopup has a second success arm (wallet-tools.ts:337)
       * where the card was charged but crediting Atlas failed: it carries
       * `paymentIntentId` and a `reason`, and no balance. Requiring the field
       * would turn that arm — a real charge the caller must be told about —
       * into a validation error.
       */
      outputSchema: {
        org_id: z.string(),
        charged: z.literal(true),
        credited_eur_cents: z.number().int(),
        balance_after_cents: z
          .number()
          .int()
          .optional()
          .describe("Absent when the charge succeeded but crediting is pending."),
        payment_intent_id: z.string().optional(),
        note: z
          .string()
          .optional()
          .describe("Present when the charge landed but the credit is still pending."),
      },
      annotations: annotationsFor("wallet_auto_topup", "write"),
    },
    async ({ pack_cents }) => {
      // Call-time scope check. This file does not use wrapHandler,
      // so without this the tool is callable at any scope. See
      // scope-guard.ts.
      const denied = denyUnlessScoped(props, "wallet_auto_topup", WALLET_TOOL_SCOPES);
      if (denied) return denied;

      const orgId = defaultOrgId;
      const start = Date.now();
      try {
        const result = await runAutoTopup(env, { orgId, sub: props.sub, packCents: pack_cents });
        writeAudit(env, {
          tool_name: "wallet_auto_topup",
          ocs_method: "stripe.payment_intent.off_session",
          status: result.charged ? "ok" : "error",
          dry_run: false,
          duration_ms: Date.now() - start,
          sub: props.sub,
          reseller_id: props.reseller_id,
        });
        if (!result.charged) {
          return err(
            JSON.stringify({ org_id: orgId, charged: false, reason: result.reason }),
          );
        }
        return okStructured({
          org_id: orgId,
          charged: true,
          credited_eur_cents: result.creditedCents,
          // Spread rather than assign, so the key is absent instead of set to
          // `undefined`. JSON.stringify drops an undefined value anyway, so
          // assigning it would make `content` and `structuredContent` disagree
          // about whether the field is there.
          ...(result.balanceAfterCents !== undefined
            ? { balance_after_cents: result.balanceAfterCents }
            : {}),
          ...(result.paymentIntentId !== undefined
            ? { payment_intent_id: result.paymentIntentId }
            : {}),
          ...(result.reason ? { note: result.reason } : {}),
        });
      } catch (e) {
        Sentry.captureException(e);
        return err(`Error: ${e instanceof Error ? e.message : "unknown"}`);
      }
    },
  );
}
